import { useCallback, useEffect, useState } from "react";

/** Upper bound on how long the content can be held back. */
const MAX_WAIT_MS = 1000;

/**
 * Ref callback paired with a flag that flips to `true` once the animations
 * running on the element have finished. Settles right away when nothing is
 * animating, so it stays correct under `prefers-reduced-motion`.
 *
 * Use it to hold back expensive content until the element it lives in has
 * finished animating in — committing a heavy subtree mid-animation is a long
 * enough main thread task to drop frames.
 *
 * @returns [ref, settled] - ref: put on the animating element, settled: whether
 * its animations have finished
 *
 * @example
 * const [contentRef, settled] = useAnimationsSettled();
 *
 * return (
 *   <SheetContent ref={contentRef}>
 *     {settled ? <ExpensiveContent /> : <Skeleton />}
 *   </SheetContent>
 * );
 */
export function useAnimationsSettled() {
  const [settled, setSettled] = useState(false);

  const ref = useCallback((el: HTMLElement | null) => {
    if (!el) return;

    const element = el;
    let framesWaited = 0;

    // Base UI starts its enter transition by clearing `data-starting-style` in
    // an animation frame of its own, so on the frame we mount there is nothing
    // running yet. Give it a few frames before concluding that nothing is
    // animating, otherwise we settle immediately and mount the content right in
    // the middle of the transition — the thing this is here to prevent.
    function check() {
      const running = element.getAnimations(); // not `subtree` — animate-pulse never finishes

      if (running.length) {
        Promise.allSettled(running.map((a) => a.finished)).then(() =>
          setSettled(true)
        );
        return;
      }

      if (framesWaited++ < 2) return requestAnimationFrame(check);

      setSettled(true);
    }

    requestAnimationFrame(check);
  }, []);

  // Fail open. Everything above hangs off the ref callback, so anything that
  // stops it from running — a component that doesn't forward its ref, an
  // element that never mounts — would gate the content out permanently. Late
  // content beats no content.
  useEffect(() => {
    const timeout = setTimeout(() => setSettled(true), MAX_WAIT_MS);

    return () => clearTimeout(timeout);
  }, []);

  return [ref, settled] as const;
}
